import { createSHA256, IHasher } from "hash-wasm";

interface HashProgress {
  progress: number;
  throughput: number;
}

export class HashService {
  private hasher: IHasher | null = null;
  private readonly chunkSize = 64 * 1024 * 1024; // 64MB chunks

  private async initHasher(): Promise<void> {
    if (!this.hasher) {
      this.hasher = await createSHA256();
    }
    this.hasher.init();
  }

  private async hashChunk(chunk: Blob): Promise<void> {
    const buffer = await chunk.arrayBuffer();
    const view = new Uint8Array(buffer);
    this.hasher?.update(view);
  }

  async hashFile(
    file: File,
    onProgress?: (progress: HashProgress) => void
  ): Promise<string> {
    await this.initHasher();

    const startTime = Date.now();
    const totalChunks = Math.ceil(file.size / this.chunkSize);

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(
        this.chunkSize * i,
        Math.min(this.chunkSize * (i + 1), file.size)
      );

      await this.hashChunk(chunk);

      if (onProgress) {
        const progress = (i + 1) / totalChunks;
        const duration = (Date.now() - startTime) / 1000; // in seconds
        const processedSize =
          Math.min((i + 1) * this.chunkSize, file.size) / (1024 * 1024); // in MB
        const throughput = processedSize / duration; // MB/s

        onProgress({
          progress,
          throughput,
        });
      }
    }

    return this.hasher?.digest("hex") || "";
  }
}

export const hashService = new HashService();
